import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Container, Row, Col, Image, Button, Tab, Tabs, Card, ListGroup, Spinner, Alert } from 'react-bootstrap';
import axios from 'axios';
import { FaCar, FaMotorcycle, FaTachometerAlt, FaMoneyBillWave, FaCalendarAlt, FaStar } from 'react-icons/fa';

const VehicleDetailPage = () => {
  const { id } = useParams();
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeImage, setActiveImage] = useState(0);

  useEffect(() => {
    const fetchVehicle = async () => {
      try {
        const { data } = await axios.get(`https://backend.stu89674.webhosting.arden.ac.uk/api/vehicles/${id}`);
        setVehicle(data);
        setLoading(false);
      } catch (err) {
        setError(err.response?.data?.message || 'Error fetching vehicle details');
        setLoading(false);
      }
    };

    fetchVehicle();
  }, [id]);

  if (loading) {
    return (
      <Container className="text-center py-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-5">
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  }

  if (!vehicle) {
    return (
      <Container className="py-5">
        <Alert variant="warning">Vehicle not found</Alert>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <Row>
        <Col lg={7}>
          <Image 
            src={vehicle.images[activeImage]?.image_url || 'https://via.placeholder.com/800x600'} 
            alt={`${vehicle.make} ${vehicle.model}`} 
            fluid 
            className="vehicle-detail-image mb-3"
          />
          
          <div className="d-flex mb-4">
            {vehicle.images.map((img, index) => (
              <Image
                key={img.image_id}
                src={img.image_url || 'https://via.placeholder.com/800x600'}
                alt={`Thumbnail ${index + 1}`}
                className={`thumbnail-image ${index === activeImage ? 'active' : ''}`}
                onClick={() => setActiveImage(index)}
              />
            ))}
          </div>

          <Tabs defaultActiveKey="details" id="vehicle-tabs" className="mb-4">
            <Tab eventKey="details" title="Details">
              <h4 className="mt-3">Vehicle Description</h4>
              <p>
                This {vehicle.year} {vehicle.make} {vehicle.model} is in {vehicle.vehicle_condition} condition 
                with only {vehicle.mileage.toLocaleString()} miles. {vehicle.details?.body_type && 
                `It features a ${vehicle.details.body_type} body style.`}
              </p>

              <h4 className="mt-4">Features</h4>
              <Row>
                {vehicle.features.map((feature, index) => (
                  <Col key={index} sm={6} md={4}>
                    <div className="d-flex align-items-center mb-3">
                      <FaStar className="text-warning me-2" />
                      <span>{feature.feature_name}: {feature.feature_value}</span>
                    </div>
                  </Col>
                ))}
              </Row>
            </Tab>
            <Tab eventKey="specs" title="Specifications">
              <ListGroup variant="flush" className="mt-3">
                <ListGroup.Item>
                  <div className="d-flex justify-content-between">
                    <span><FaCar className="me-2" />Make</span>
                    <span>{vehicle.make}</span>
                  </div>
                </ListGroup.Item>
                <ListGroup.Item>
                  <div className="d-flex justify-content-between">
                    <span><FaCar className="me-2" />Model</span>
                    <span>{vehicle.model}</span>
                  </div>
                </ListGroup.Item>
                <ListGroup.Item>
                  <div className="d-flex justify-content-between">
                    <span><FaCalendarAlt className="me-2" />Year</span>
                    <span>{vehicle.year}</span>
                  </div>
                </ListGroup.Item>
                <ListGroup.Item>
                  <div className="d-flex justify-content-between">
                    <span><FaTachometerAlt className="me-2" />Mileage</span>
                    <span>{vehicle.mileage.toLocaleString()} miles</span>
                  </div>
                </ListGroup.Item>
                {vehicle.type === 'car' && (
                  <>
                    <ListGroup.Item>
                      <div className="d-flex justify-content-between">
                        <span><FaCar className="me-2" />Body Type</span>
                        <span>{vehicle.details.body_type}</span>
                      </div>
                    </ListGroup.Item>
                    <ListGroup.Item>
                      <div className="d-flex justify-content-between">
                        <span><FaCar className="me-2" />Doors</span>
                        <span>{vehicle.details.doors}</span>
                      </div>
                    </ListGroup.Item>
                    <ListGroup.Item>
                      <div className="d-flex justify-content-between">
                        <span><FaCar className="me-2" />Transmission</span>
                        <span>{vehicle.details.transmission}</span>
                      </div>
                    </ListGroup.Item>
                  </>
                )}
                {vehicle.type === 'motorcycle' && (
                  <>
                    <ListGroup.Item>
                      <div className="d-flex justify-content-between">
                        <span><FaMotorcycle className="me-2" />Engine Size</span>
                        <span>{vehicle.details.engine_size}cc</span>
                      </div>
                    </ListGroup.Item>
                    <ListGroup.Item>
                      <div className="d-flex justify-content-between">
                        <span><FaMotorcycle className="me-2" />Type</span>
                        <span>{vehicle.details.type}</span>
                      </div>
                    </ListGroup.Item>
                    <ListGroup.Item>
                      <div className="d-flex justify-content-between">
                        <span><FaMotorcycle className="me-2" />Sidecar</span>
                        <span>{vehicle.details.has_sidecar ? 'Yes' : 'No'}</span>
                      </div>
                    </ListGroup.Item>
                  </>
                )}
              </ListGroup>
            </Tab>
          </Tabs>
        </Col>

        <Col lg={5}>
          <Card className="sticky-top" style={{ top: '20px' }}>
            <Card.Body>
              <Card.Title className="display-6">
                {vehicle.year} {vehicle.make} {vehicle.model}
              </Card.Title>
              <Card.Subtitle className="mb-3 text-muted">
                {vehicle.type === 'car' ? 'Car' : 'Motorcycle'}
              </Card.Subtitle>
              
              <div className="d-flex align-items-center mb-4">
                <span className="display-4 text-primary fw-bold">
                  ${vehicle.price.toLocaleString()}
                </span>
              </div>

              <ListGroup variant="flush" className="mb-4">
                <ListGroup.Item>
                  <div className="d-flex justify-content-between">
                    <span>Condition</span>
                    <span className="text-capitalize">{vehicle.vehicle_condition}</span>
                  </div>
                </ListGroup.Item>
                <ListGroup.Item>
                  <div className="d-flex justify-content-between">
                    <span>Mileage</span>
                    <span>{vehicle.mileage.toLocaleString()} miles</span>
                  </div>
                </ListGroup.Item>
                <ListGroup.Item>
                  <div className="d-flex justify-content-between">
                    <span>Location</span>
                    <span>New York, NY</span>
                  </div>
                </ListGroup.Item>
              </ListGroup>

              <Button variant="primary" size="lg" className="w-100 mb-3">
                Contact Seller
              </Button>
              <Button variant="outline-primary" size="lg" className="w-100">
                Make an Offer
              </Button>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default VehicleDetailPage;